import { useMsal } from "@azure/msal-react";
import { StarRatingInput, TextAreaInput } from "@components/FormInputs";
import Spinner from "@components/Spinner";
import { useMemo, useRef, useState } from "react";
import {
  useCreateUserFeedbackMutation,
  type CreateUserFeedbackPayload,
  type CreateUserFeedbackResponse,
} from "../../api/user-feedback-api";
import { useSelectedSites } from "../../hooks";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Icon from "../Icon";
import Chip from "./Chip";
import styles from "./FeedbackForm.module.css";

type FeedbackFormProps = {
  onCancel: () => void;
  onSubmit?: (feedback: string) => void;
  submitFeedback?: (
    payload: CreateUserFeedbackPayload
  ) => Promise<CreateUserFeedbackResponse>;
  feedbackOptional?: boolean;
  ratingPrompt?: string;
  description?: string;
  titleId?: string;
  disabled?: boolean;
};

function getBrowser(): string {
  const ua = navigator.userAgent;
  const browsers: { name: string; regex: RegExp }[] = [
    { name: "Edge", regex: /Edg\/([\d]+)/ },
    { name: "Chrome", regex: /Chrome\/([\d]+)/ },
    { name: "Firefox", regex: /Firefox\/([\d]+)/ },
    { name: "Safari", regex: /Version\/([\d]+).*Safari/ },
  ];
  for (const { name, regex } of browsers) {
    const match = ua.match(regex);
    if (match) return `${name} ${match[1]}`;
  }
  return "Unknown browser";
}

function FeedbackForm({
  onCancel,
  onSubmit,
  submitFeedback,
  feedbackOptional = false,
  ratingPrompt = "How is this service working for you?",
  description,
  titleId,
  disabled = false,
}: FeedbackFormProps) {
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [allowContact, setAllowContact] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const { mutateAsync: createUserFeedback, isPending } =
    useCreateUserFeedbackMutation(submitFeedback);
  const { accounts } = useMsal();
  const { selectedSites } = useSelectedSites();

  const capturedInfo = useMemo(() => {
    const moduleEnv = import.meta.env.VITE_MODULE_NAME ?? "Unknown module";
    const moduleName = moduleEnv === "Connect" ? "Landing Page" : moduleEnv;

    const now = new Date();
    const hh = String(now.getUTCHours()).padStart(2, "0");
    const mm = String(now.getUTCMinutes()).padStart(2, "0");
    return {
      timeUtc: `${hh}:${mm} UTC`,
      userEmail: accounts?.length > 0 ? accounts[0].username : "Unknown user",
      module: moduleName,
      browser: getBrowser(),
      appVersion: import.meta.env.VITE_APP_VERSION ?? "Unknown version",
      selectedSites,
    };
  }, [accounts, selectedSites]);

  const capturedInfoChips = useMemo(
    () => [
      { icon: "Time", value: capturedInfo.timeUtc },
      { icon: "User", value: capturedInfo.userEmail },
      { icon: "Document", value: capturedInfo.module },
      { icon: "Devices", value: capturedInfo.browser },
      { icon: "Label", value: capturedInfo.appVersion },
    ],
    [capturedInfo]
  );

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedFeedback = feedback.trim();
    if (
      submitting.current ||
      disabled ||
      rating === 0 ||
      (!feedbackOptional && !trimmedFeedback)
    )
      return;
    submitting.current = true;
    setError("");
    try {
      await createUserFeedback({
        feedback: trimmedFeedback,
        rating,
        allowContact,
        capturedInfo,
      });
      setIsSubmitted(true);
      onSubmit?.(trimmedFeedback);
    } catch {
      setError("Feedback could not be sent. Please try again.");
    } finally {
      submitting.current = false;
    }
  };

  if (isSubmitted) {
    return <FeedbackSentConfirmation onClose={onCancel} titleId={titleId} />;
  }

  return (
    <div className={styles.container}>
      <div
        className={`${styles.content} ${isPending ? styles.pendingContent : ""}`}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            Give feedback to Grinding Connect
          </h2>
          <Button
            variant="transparent"
            icon="Close"
            onClick={() => {
              if (!submitting.current) onCancel();
            }}
            aria-label="Close feedback form"
            type="button"
            size="extra-small"
            disabled={isPending}
          />
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.section}>
            <p className={styles.prompt}>{ratingPrompt}</p>
            <StarRatingInput value={rating} onChange={setRating} />
          </div>

          <div className={styles.section}>
            <TextAreaInput
              name="feedback-text"
              label={feedbackOptional ? "Feedback (optional)" : "Feedback"}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              showErrors={false}
              maxLength={4000}
            />
          </div>

          <div className={styles.section}>
            <Checkbox
              text="You can contact me about this feedback"
              checked={allowContact}
              setChecked={setAllowContact}
            />
          </div>

          <div className={styles.section}>
            <p className={styles.captureTitle}>Captured automatically</p>
            <div className={styles.chips}>
              {capturedInfoChips.map((info) => (
                <Chip key={info.icon} icon={info.icon}>
                  {info.value}
                </Chip>
              ))}
            </div>
          </div>

          {description && <p className={styles.prompt}>{description}</p>}
          {error && <p role="alert">{error}</p>}

          <div className={styles.actions}>
            <Button
              type="submit"
              variant="secondary"
              text="Send feedback"
              disabled={
                disabled ||
                isPending ||
                (!feedbackOptional && !feedback.trim()) ||
                rating === 0
              }
            />
          </div>
        </form>
      </div>
      {isPending && (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      )}
    </div>
  );
}

function FeedbackSentConfirmation({
  onClose,
  titleId,
}: {
  onClose: () => void;
  titleId?: string;
}) {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div />
        <button
          type="button"
          autoFocus
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close feedback form"
        >
          <Icon name="Close" size="1rem" />
        </button>
      </div>
      <div className={styles.successBody}>
        <span className={styles.successHeader}>
          <Icon name="Check_mark_circle_1" className={styles.successIcon} />
          <p id={titleId} className={styles.successTitle}>
            Feedback sent
          </p>
        </span>
        <p className={styles.successSubtitle}>
          Help us improve.{" "}
          <a
            href="https://forms.office.com/Pages/ResponsePage.aspx?id=4OkuN-CcM0CmSsBwc6kezU3AjqdcuJFOi2fLlmxSooRUM0tSTUtPTFBaTVVMMUNJV01YRlNGWENOWi4u"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.successLink}
          >
            Share more of your thoughts in a short survey
          </a>
        </p>
      </div>
    </div>
  );
}

export default FeedbackForm;
